import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';

const Header = () => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Use stable keys for navigation items
  const navigation = [
    { key: 'home', nameKey: 'nav.home', href: '/#home' },
    { key: 'about', nameKey: 'nav.about', href: '/#about' },
    { key: 'services', nameKey: 'nav.services', href: '/#services' },
    { key: 'contact', nameKey: 'nav.contact', href: '/#contact' }
  ];

  useEffect(() => {
    setIsLoaded(true);
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: 2.2 + i * 0.1,
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1],
      },
    }),
  };

  return (
    <motion.header
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 2, ease: [0.25, 0.1, 0.25, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700`}
    >
      {/* Liquid Glass Container */}
      <div
        className={`mx-4 mt-4 rounded-2xl transition-all duration-700 ${
          isScrolled
            ? 'bg-white/40 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.08)] border border-white/50'
            : 'bg-white/20 backdrop-blur-xl border border-white/30'
        }`}
        style={{
          background: isScrolled 
            ? 'linear-gradient(135deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.3) 100%)'
            : 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <nav className="flex items-center justify-between h-16">
            {/* Left Navigation */}
            <div className="hidden md:flex items-center gap-6">
              {navigation.slice(0, 2).map((item, i) => (
                <motion.a
                  key={item.key}
                  href={item.href}
                  custom={i}
                  initial="hidden"
                  animate={isLoaded ? "visible" : "hidden"}
                  variants={navVariants}
                  className="relative px-3 py-1.5 text-navy/80 font-light text-sm tracking-wide hover:text-navy transition-all duration-300 rounded-lg hover:bg-white/30"
                  whileHover={{ y: -2 }}
                >
                  {t(item.nameKey)}
                </motion.a>
              ))}
            </div>

            {/* Center Logo */}
            <motion.a
              href="/#home"
              className="flex items-center justify-center group absolute left-1/2 -translate-x-1/2"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 2.1, ease: [0.25, 0.1, 0.25, 1] }}
              whileHover={{ scale: 1.05 }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_glass-portfolio-27/artifacts/a8c3zkhn_logo_full.png"
                alt="Limitless Solutions Logo"
                className="h-12 w-auto"
              />
            </motion.a>

            {/* Right Navigation */}
            <div className="hidden md:flex items-center gap-4">
              {navigation.slice(2).map((item, i) => (
                <motion.a
                  key={item.key}
                  href={item.href}
                  custom={i + 2}
                  initial="hidden"
                  animate={isLoaded ? "visible" : "hidden"}
                  variants={navVariants}
                  className="relative px-3 py-1.5 text-navy/80 font-light text-sm tracking-wide hover:text-navy transition-all duration-300 rounded-lg hover:bg-white/30"
                  whileHover={{ y: -2 }}
                >
                  {t(item.nameKey)}
                </motion.a>
              ))}
              <motion.a
                href="/blog"
                custom={4}
                initial="hidden"
                animate={isLoaded ? "visible" : "hidden"}
                variants={navVariants}
                className="relative px-3 py-1.5 text-navy/80 font-light text-sm tracking-wide hover:text-navy transition-all duration-300 rounded-lg hover:bg-white/30"
                whileHover={{ y: -2 }}
              >
                {t('nav.blog')}
              </motion.a>
              
              {/* Language Switcher */}
              <motion.div
                custom={5}
                initial="hidden"
                animate={isLoaded ? "visible" : "hidden"}
                variants={navVariants}
              >
                <LanguageSwitcher />
              </motion.div>

              <motion.a
                href="/#contact"
                custom={6}
                initial="hidden"
                animate={isLoaded ? "visible" : "hidden"}
                variants={navVariants}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="liquid-glass-button px-5 py-2 text-sm font-light tracking-wide">
                  {t('nav.contact')}
                </span>
              </motion.a>
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              className="md:hidden text-navy p-2 ml-auto rounded-xl bg-white/20 backdrop-blur-md"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              whileTap={{ scale: 0.95 }}
            >
              <AnimatePresence mode="wait">
                {mobileMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X size={22} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu size={22} />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </nav>

          {/* Mobile Navigation */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="md:hidden overflow-hidden"
              >
                <div className="flex flex-col gap-2 py-4 border-t border-white/20">
                  {navigation.map((item, i) => (
                    <motion.a
                      key={item.key}
                      href={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className="px-4 py-2 text-navy/80 font-light text-base tracking-wide hover:text-navy hover:bg-white/20 rounded-xl transition-all duration-300"
                    >
                      {t(item.nameKey)}
                    </motion.a>
                  ))}
                  <motion.a
                    href="/blog"
                    onClick={() => setMobileMenuOpen(false)}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: navigation.length * 0.1 }}
                    className="px-4 py-2 text-navy/80 font-light text-base tracking-wide hover:text-navy hover:bg-white/20 rounded-xl transition-all duration-300"
                  >
                    {t('nav.blog')}
                  </motion.a>
                  
                  {/* Mobile Language Switcher */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: (navigation.length + 1) * 0.1 }}
                    className="px-4 py-2"
                  >
                    <LanguageSwitcher />
                  </motion.div>

                  <motion.a
                    href="/#contact"
                    onClick={() => setMobileMenuOpen(false)}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: (navigation.length + 2) * 0.1 }}
                    className="mx-4 mt-2"
                  >
                    <span className="liquid-glass-button w-full py-2.5 text-sm font-light tracking-wide text-center">
                      {t('nav.contact')}
                    </span>
                  </motion.a>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.header>
  );
};

export default Header;
